import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import FretQuoteForm from '../components/FretQuoteForm';
import FretPriceSimulator from '../components/FretPriceSimulator';
import { Card, CardContent } from '../components/ui/card';
import {
  Ship, CheckCircle, Truck, FileCheck, MapPin, Home, ShieldCheck, Clock, Package
} from 'lucide-react';

/**
 * SEO landing pages : /fret/:slug
 * Each slug targets a specific search intent like "envoyer moto guadeloupe".
 */

const SEO_PAGES = {
  // ===== Antilles =====
  'moto-guadeloupe': {
    title: 'Envoyer une moto en Guadeloupe',
    h1: 'Transport de moto vers la Guadeloupe',
    metaDesc: 'Envoyez votre moto en Guadeloupe en toute sécurité. Enlèvement partout en France, transport maritime, dédouanement inclus. Devis gratuit 24h.',
    fromPrice: '850 €',
    deliveryTime: '15-25 jours',
    vehicleType: 'moto',
    destination: 'Guadeloupe',
    intro: "Vous habitez en Guadeloupe ou venez d'y acheter une moto en métropole ? ISMParcel s'occupe de tout : enlèvement à votre adresse en France, transport maritime sécurisé jusqu'à Pointe-à-Pitre, dédouanement, et livraison finale.",
    benefits: [
      'Enlèvement de votre moto partout en France métropolitaine',
      'Transport maritime en RoRo ou conteneur sécurisé',
      'Dédouanement et formalités douanières inclus',
      'Assurance cargo couvrant la valeur déclarée',
      'Suivi de votre moto en temps réel',
      'Livraison à Pointe-à-Pitre ou Basse-Terre selon votre choix',
    ],
  },
  'moto-martinique': {
    title: 'Envoyer une moto en Martinique',
    h1: 'Transport de moto vers la Martinique',
    metaDesc: 'Service complet pour envoyer votre moto en Martinique. Enlèvement, transport maritime, dédouanement, livraison à Fort-de-France. Devis 24h.',
    fromPrice: '850 €',
    deliveryTime: '15-25 jours',
    vehicleType: 'moto',
    destination: 'Martinique',
    intro: "Acheminez votre moto vers la Martinique en toute simplicité. ISMParcel prend en charge l'intégralité du transport, du chargement en métropole jusqu'à la livraison à Fort-de-France ou ailleurs sur l'île.",
    benefits: [
      'Enlèvement à domicile partout en France',
      'Transport maritime régulier vers Fort-de-France',
      'Toutes formalités douanières incluses',
      'Assurance transport tous risques',
      'Suivi en ligne',
      'Livraison finale au port ou à domicile',
    ],
  },
  'moto-reunion': {
    title: 'Envoyer une moto à La Réunion',
    h1: 'Transport de moto vers La Réunion',
    metaDesc: 'Expédition de moto vers La Réunion : enlèvement France, conteneur maritime, dédouanement, livraison Saint-Denis ou Saint-Pierre. Devis gratuit.',
    fromPrice: '900 €',
    deliveryTime: '20-30 jours',
    vehicleType: 'moto',
    destination: 'La Réunion',
    intro: "Vous résidez à La Réunion ou vous y installez bientôt ? Faites transporter votre moto par ISMParcel. Service tout compris, du domicile en métropole à votre adresse à La Réunion.",
    benefits: [
      'Enlèvement national en France',
      'Mise en conteneur sécurisé',
      "Dédouanement à l'arrivée",
      'Assurance cargo',
      'Livraison à votre adresse à La Réunion',
    ],
  },
  // ===== Polynésie =====
  'moto-polynesie': {
    title: 'Envoyer une moto en Polynésie française',
    h1: 'Transport de moto vers Tahiti et la Polynésie',
    metaDesc: 'Envoyez votre moto à Tahiti, Bora Bora, Moorea... Service complet ISMParcel : enlèvement, transport maritime, douane, livraison Papeete. Devis 24h.',
    fromPrice: '1 400 €',
    deliveryTime: '35-50 jours',
    vehicleType: 'moto',
    destination: 'Polynésie française',
    intro: "Acheminer une moto vers la Polynésie demande de la rigueur logistique. ISMParcel maîtrise cette destination lointaine et vous accompagne de A à Z, jusqu'à Papeete ou aux îles voisines.",
    benefits: [
      'Spécialiste DOM-TOM et destinations lointaines',
      'Conteneur maritime sécurisé vers Papeete',
      'Dédouanement intégral pris en charge',
      'Assurance valeur déclarée',
      'Livraison à Tahiti ou aux îles selon votre besoin',
    ],
  },
  'voiture-polynesie': {
    title: 'Envoyer une voiture en Polynésie française',
    h1: 'Transport de voiture vers Tahiti',
    metaDesc: 'Envoyez votre voiture à Tahiti et en Polynésie. RoRo ou conteneur, dédouanement inclus, livraison à Papeete. Devis sans engagement 24h.',
    fromPrice: '2 500 €',
    deliveryTime: '40-55 jours',
    vehicleType: 'voiture',
    destination: 'Polynésie française',
    intro: "Vous emménagez à Tahiti ou venez d'acheter votre voiture en France ? ISMParcel transporte votre véhicule vers la Polynésie en toute sécurité, avec dédouanement et livraison finale.",
    benefits: [
      'Transport en RoRo ou conteneur dédié',
      'Dédouanement Polynésie inclus',
      'Assurance cargo tous risques',
      'Suivi en temps réel',
      'Livraison à Papeete ou autres îles',
    ],
  },
  // ===== Voiture vers Antilles =====
  'voiture-guadeloupe': {
    title: 'Envoyer une voiture en Guadeloupe',
    h1: 'Transport de voiture vers la Guadeloupe',
    metaDesc: 'Expédition de voiture en Guadeloupe : enlèvement France, conteneur maritime, dédouanement, livraison Pointe-à-Pitre. Tarif à partir de 1500€.',
    fromPrice: '1 500 €',
    deliveryTime: '15-25 jours',
    vehicleType: 'voiture',
    destination: 'Guadeloupe',
    intro: "Faites livrer votre voiture en Guadeloupe sereinement. Du chargement en métropole jusqu'à votre garage à Pointe-à-Pitre ou Basse-Terre, ISMParcel gère tout.",
    benefits: [
      'Enlèvement national',
      'Transport maritime sécurisé',
      'Toutes formalités douanières',
      'Assurance cargo',
      'Livraison Guadeloupe',
    ],
  },
  'voiture-martinique': {
    title: 'Envoyer une voiture en Martinique',
    h1: 'Transport de voiture vers la Martinique',
    metaDesc: 'Envoyez votre voiture en Martinique avec ISMParcel. Service complet de l\'enlèvement à la livraison. Tarifs à partir de 1500€.',
    fromPrice: '1 500 €',
    deliveryTime: '15-25 jours',
    vehicleType: 'voiture',
    destination: 'Martinique',
    intro: "Transport de votre voiture vers la Martinique en toute confiance. Service tout-inclus : enlèvement, transport maritime, douane, livraison à Fort-de-France ou ailleurs.",
    benefits: [
      'Enlèvement partout en France',
      'Conteneur ou RoRo sécurisé',
      'Dédouanement Martinique',
      'Assurance valeur déclarée',
      'Livraison Fort-de-France',
    ],
  },
  // ===== Jet-ski =====
  'jet-ski-reunion': {
    title: 'Envoyer un jet-ski à La Réunion',
    h1: 'Transport de jet-ski vers La Réunion',
    metaDesc: 'Expédition de jet-ski vers La Réunion. Enlèvement France, transport maritime sécurisé, dédouanement, livraison. Devis gratuit 24h.',
    fromPrice: '1 200 €',
    deliveryTime: '20-30 jours',
    vehicleType: 'jetski',
    destination: 'La Réunion',
    intro: "Acheminez votre jet-ski vers La Réunion avec un transporteur de confiance. ISMParcel manipule votre engin avec soin et l'emballe pour le transport maritime.",
    benefits: [
      'Manutention spécialisée',
      'Conteneur sécurisé',
      'Dédouanement inclus',
      'Assurance cargo',
      'Livraison à La Réunion',
    ],
  },
  'jet-ski-guadeloupe': {
    title: 'Envoyer un jet-ski en Guadeloupe',
    h1: 'Transport de jet-ski vers la Guadeloupe',
    metaDesc: 'Envoyez votre jet-ski en Guadeloupe en toute sécurité. Service complet ISMParcel. Tarif à partir de 1100€. Devis 24h.',
    fromPrice: '1 100 €',
    deliveryTime: '15-25 jours',
    vehicleType: 'jetski',
    destination: 'Guadeloupe',
    intro: "Faites transporter votre jet-ski vers la Guadeloupe avec ISMParcel. Manutention spécialisée, transport maritime sécurisé, dédouanement et livraison.",
    benefits: [
      'Manutention experte',
      'Transport sécurisé',
      'Dédouanement inclus',
      'Assurance cargo',
      'Livraison Guadeloupe',
    ],
  },
  // ===== Voiture Réunion =====
  'voiture-reunion': {
    title: 'Envoyer une voiture à La Réunion',
    h1: 'Transport de voiture vers La Réunion',
    metaDesc: 'Expédition de voiture vers La Réunion : enlèvement France, conteneur maritime, dédouanement, livraison Saint-Denis. Devis gratuit.',
    fromPrice: '1 800 €',
    deliveryTime: '20-30 jours',
    vehicleType: 'voiture',
    destination: 'La Réunion',
    intro: "Faites livrer votre voiture à La Réunion en toute sécurité. ISMParcel gère le transport intégral, du domicile en métropole à votre garage à La Réunion.",
    benefits: [
      'Enlèvement national',
      'Conteneur maritime sécurisé',
      'Dédouanement Réunion',
      'Assurance cargo',
      'Livraison Saint-Denis ou Saint-Pierre',
    ],
  },
};

const PROCESS = [
  { icon: FileCheck, title: '1. Demande de devis', desc: 'Remplissez le formulaire en 2 min. Devis personnalisé sous 24h ouvrées.' },
  { icon: Truck, title: '2. Enlèvement', desc: 'Nous récupérons votre véhicule partout en France à la date convenue.' },
  { icon: Ship, title: '3. Transport maritime', desc: 'Acheminement vers le port et traversée sécurisée en conteneur ou RoRo.' },
  { icon: MapPin, title: '4. Dédouanement', desc: 'Formalités douanières prises en charge à l\'arrivée.' },
  { icon: Home, title: '5. Livraison', desc: 'Livraison finale au port ou à domicile selon votre choix.' },
];

export default function FretDestinationPage() {
  const { slug } = useParams();
  const config = SEO_PAGES[slug];

  // Update document title and meta description for SEO (must be before any conditional return)
  React.useEffect(() => {
    if (!config) return;
    document.title = `${config.title} | ISMParcel — À partir de ${config.fromPrice}`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', config.metaDesc);
  }, [config]);

  if (!config) {
    return <Navigate to="/fret" replace />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="fret-destination-page">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #60a5fa 0%, transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 border border-amber-400/30 text-amber-200 rounded-full text-sm font-semibold mb-6">
                <Ship className="w-4 h-4" />
                Service spécialisé
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-5xl tracking-tight mb-6 leading-tight">
                {config.h1}
              </h1>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">{config.intro}</p>
              <div className="flex flex-wrap gap-6 text-sm mb-6">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-300" />
                  <span><strong className="text-white">{config.deliveryTime}</strong> de transit</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <span>Assurance incluse</span>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4 inline-block">
                <div className="text-xs text-slate-400 uppercase tracking-wide font-semibold">À partir de</div>
                <div className="font-heading font-bold text-3xl text-white">{config.fromPrice}</div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
              <FretPriceSimulator defaultVehicle={config.vehicleType} defaultDestination={config.destination} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 tracking-tight mb-8">
            Pourquoi choisir ISMParcel ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl">
                <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 text-center tracking-tight mb-12">
            Comment ça marche ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-200">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-heading font-bold text-slate-900 mb-2">{p.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="devis" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 tracking-tight mb-3">
              Demandez votre devis gratuit
            </h2>
            <p className="text-lg text-slate-600">Réponse personnalisée sous 24h ouvrées.</p>
          </div>
          <FretQuoteForm />
        </div>
      </section>

      {/* Other destinations */}
      <section className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-heading font-bold text-xl text-slate-900 mb-4 text-center">Autres destinations populaires</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {Object.entries(SEO_PAGES)
              .filter(([s]) => s !== slug)
              .slice(0, 8)
              .map(([s, c]) => (
                <Link
                  key={s}
                  to={`/fret/${s}`}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-700 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  {c.title}
                </Link>
              ))}
            <Link to="/fret" className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-semibold hover:bg-blue-700">
              Voir tous nos services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export { SEO_PAGES };
